import cx from 'classnames2';
import React, { useEffect, useState } from 'react';

import { IBaseProps } from '../utils';

import { Button } from '../Button';
import { ButtonGroup } from '../ButtonGroup';
import { Icon } from '../Icon';
import { Label } from '../Label';
import { ISelectOptionProps, ISelectProps, Select } from '../Select';
import { Text } from '../Text';

import styles from './Pagination.module.scss';
import { usePagination } from './usePagination';

const format = (num: number) => num.toLocaleString('en-US');

export const getShowingText = (currentRowsPerPage: number, page: number, itemName: string, totalRows: number) => {
  const startRowIndex = page === 1 ? 1 : currentRowsPerPage * (page - 1) + 1;
  const endRowIndex = currentRowsPerPage * page > totalRows ? totalRows : currentRowsPerPage * page;

  return `Showing ${format(startRowIndex)}-${format(endRowIndex)} of ${format(totalRows)} ${itemName}`;
};

export const getItemAriaLabel = (type: string, page: number, selected: boolean) => {
  if (type === 'page') {
    return `${selected ? 'Page' : 'Go to page'} ${page.toLocaleString('en-US')}`;
  }
};

export interface IPaginationProps extends IBaseProps {
  /** Number of always visible pages at the beginning and end. */
  boundaryCount?: number;
  /** The current page. */
  currentPage: number;
  /** Text that represents the items in each page. */
  itemName?: string;
  /** Default number of rows to show per page.
   * This is also used to generate select options in multiples of 3 if 'rowsPerPageOptions' is not provided.
   */
  rowsPerPage?: number;
  /** Allows you to override the default increments shown as select options. */
  rowsPerPageOptions?: ISelectOptionProps[];
  /** Allows you to hide the rowsPerPage select in cases where that is static. */
  shouldShowRowsPerPageSelect?: boolean;
  /** Number of always visible pages before and after the current page. */
  siblingCount?: number;
  /** The total number of rows you are paginating. */
  totalRows: number;
  /** Props that are used on the Select component */
  selectProps?: ISelectProps;
  /** Callback that gets triggered when page is changed.
   *  Use this to update the 'currentPage' in your state.
   */
  onPageChange(pageIndex: number): void;
  /** Callback that gets triggered when a new value is selected for 'rowsPerPage'. */
  onRowsPerPageChange?(rowsPerPage: number): void;
}

export const Pagination: React.FC<IPaginationProps> = ({
  boundaryCount = 1,
  currentPage = 1,
  className,
  dataTest,
  itemName = 'Items',
  rowsPerPage,
  rowsPerPageOptions,
  shouldShowRowsPerPageSelect = true,
  siblingCount = 2,
  totalRows = 100,
  onPageChange,
  onRowsPerPageChange,
  selectProps
}) => {
  // Just in case 'rowsPerPage' doesn't match any of the options passed into 'rowsPerPageOptions'
  const defaultRowsPerPage =
    rowsPerPage ||
    (rowsPerPageOptions && rowsPerPageOptions.length > 0 && rowsPerPageOptions[0] && rowsPerPageOptions[0].value) ||
    25;
  const [currentRowsPerPage, setCurrentRowsPerPage] = useState(defaultRowsPerPage);
  const totalPages = Math.ceil(totalRows / currentRowsPerPage);

  // The secret sauce
  const { items, page, setPage } = usePagination({
    boundaryCount,
    currentPage,
    siblingCount,
    totalPages,
    onPageChange
  });

  // Reset to last page if current page is out of range
  useEffect(() => {
    if (currentRowsPerPage * (page - 1) + 1 > totalRows) {
      setPage(Math.ceil(totalRows / currentRowsPerPage));
    }
  }, [currentRowsPerPage, page, totalRows]);

  const handleRowsPerPageChange = (rows: number) => {
    setCurrentRowsPerPage(rows);

    if (onRowsPerPageChange) {
      onRowsPerPageChange(rows);
    }
  };

  return (
    <div className={cx(styles.pagination, className)} data-test={dataTest ? dataTest : 'table-pagination'}>
      <div className={styles.column}>
        <nav className={styles.nav} role="navigation" aria-label="Pagination navigation">
          <Button
            className={styles.navButton}
            size="small"
            variant="neutral"
            title="Previous"
            aria-label="Previous page"
            dataTest="prev-pagination-button"
            disabled={page <= 1}
            tabIndex={0}
            onClick={(e) => setPage(page - 1)}
          >
            <Icon className={styles.leftIcon} icon="arrow-chevron-left" />
            <span>Previous</span>
          </Button>
          <ButtonGroup
            className={styles.buttonGroup}
            data-test="pagination-button-group"
            size="small"
            variant="neutral"
          >
            {items.map(({ type, page: pg, selected, onClick }, index) => {
              if (type === 'ellipsis') {
                return (
                  <Button
                    key={index}
                    disabled={true}
                    className={styles.pageButton}
                    ariaLabel={getItemAriaLabel(type, pg, selected)}
                  >
                    <span>...</span>
                  </Button>
                );
              }
              return (
                <Button
                  key={index}
                  selected={selected}
                  className={styles.pageButton}
                  tabIndex={0}
                  ariaLabel={getItemAriaLabel(type, pg, selected)}
                  aria-current={selected}
                  onClick={onClick}
                >
                  <span>{pg}</span>
                </Button>
              );
            })}
          </ButtonGroup>
          <Button
            className={styles.navButton}
            size="small"
            variant="neutral"
            title="Next"
            aria-label="Next page"
            dataTest="next-pagination-button"
            disabled={page >= totalPages}
            tabIndex={0}
            onClick={(e) => setPage(page + 1)}
          >
            <span>Next</span>
            <Icon className={styles.rightIcon} icon="arrow-chevron-right" />
          </Button>
        </nav>
        <Text dataTest="pagination-showing" className={styles.showing} size="small" font="condensed" color="light">
          {getShowingText(currentRowsPerPage, page, itemName, totalRows)}
        </Text>
      </div>
      {shouldShowRowsPerPageSelect && (
        <div className={styles.column}>
          <Label>{`${itemName} per page`}</Label>
          <Select
            className={styles.select}
            dataTest="pagination-rows-per-page-select"
            width="min"
            value={currentRowsPerPage}
            onChange={handleRowsPerPageChange}
            size="Small"
            {...selectProps}
          >
            {rowsPerPageOptions && rowsPerPageOptions.length > 0
              ? rowsPerPageOptions.map((option, index) => <Select.Option {...option} key={`rows-option-${index}`} />)
              : // By default we render 3 options that are multiples of 'rowsPerPage'
                [
                  <Select.Option key="rows-option-1" value={rowsPerPage} label={`${rowsPerPage}`} />,
                  <Select.Option key="rows-option-2" value={rowsPerPage * 2} label={`${rowsPerPage * 2}`} />,
                  <Select.Option key="rows-option-3" value={rowsPerPage * 3} label={`${rowsPerPage * 3}`} />
                ]}
          </Select>
        </div>
      )}
    </div>
  );
};
